// src/features/locker-assignments/components/forms/FormValidation.tsx
import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Hotel, LockerRequirement, ReferenceField } from '../../types';

// ============================================
// INTERFACES LOCALES
// ============================================

interface EnableWhenValidProps {
  requiredFields: string[];
  selectedHotel: Hotel | null;
  lockerRequirements: LockerRequirement[];
  referenceFields?: ReferenceField[];
  referenceValues?: Record<string, any>;
  checkCustomValidation?: boolean;
}

interface FormValidationHandlerProps {
  selectedHotel: Hotel | null;
  lockerRequirements: LockerRequirement[];
  referenceFields: ReferenceField[];
  referenceValues: Record<string, any>;
}

// ============================================
// COMPONENTE: EnableWhenValid
// ============================================

export const EnableWhenValid: React.FC<EnableWhenValidProps> = ({ 
  requiredFields,
  selectedHotel,
  lockerRequirements,
  referenceFields,
  referenceValues,
  checkCustomValidation = false 
}) => {
  const { getValues, formState, trigger, setValue } = useFormContext();
  
  React.useEffect(() => {
    // Verificar si todos los campos obligatorios tienen valor
    const checkRequiredFields = async () => {
      const values = getValues();
      
      // Verificar campos del formulario
      const allRequiredFieldsHaveValue = requiredFields.every(field => {
        const value = values[field];
        return value !== undefined && value !== null && value !== '';
      });
      
      // Verificar validaciones personalizadas
      let customValidationPassed = true;
      if (checkCustomValidation) {
        // Verificar que hay un hotel seleccionado
        customValidationPassed = customValidationPassed && !!selectedHotel;
        
        // Verificar que hay al menos una taquilla solicitada
        const hasAtLeastOneLocker = lockerRequirements.some(req => req.quantity > 0);
        customValidationPassed = customValidationPassed && hasAtLeastOneLocker;
        
        // Verificar campos de referencia obligatorios
        if (referenceFields && referenceValues) {
          const allRequiredReferencesHaveValue = referenceFields
            .filter(field => field.isMandatory)
            .every(field => {
              const value = referenceValues[field.key];
              return value !== undefined && value !== null && value !== '';
            });
          customValidationPassed = customValidationPassed && allRequiredReferencesHaveValue;
        }
      }
      
      if (allRequiredFieldsHaveValue && customValidationPassed && !formState.isDirty) {
        // Si todos los campos obligatorios tienen valor, validar y marcar como dirty
        const isValid = await trigger(requiredFields);
        if (isValid) {
          // Marcar el formulario como dirty usando setValue
          setValue('_formInitialized', true, { shouldDirty: true });
        }
      }
    };
    
    // Ejecutar la verificación después de que se carguen los valores por defecto
    const timer = setTimeout(checkRequiredFields, 100);
    
    return () => clearTimeout(timer);
  }, [
    getValues, 
    formState.isDirty, 
    trigger, 
    setValue, 
    requiredFields, 
    selectedHotel,
    lockerRequirements,
    referenceFields,
    referenceValues,
    checkCustomValidation
  ]);
  
  return null;
};

// ============================================
// HOOK: useFormValidation
// ============================================

export const useFormValidation = (
  selectedHotel: Hotel | null,
  lockerRequirements: LockerRequirement[],
  referenceFields: ReferenceField[],
  referenceValues: Record<string, any>
) => {
  const { formState, getValues, setValue } = useFormContext();
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Debounce los referenceValues
  const [debouncedReferenceValues, setDebouncedReferenceValues] = useState(referenceValues);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReferenceValues(referenceValues);
    }, 500); // Esperar 500ms después de que el usuario deje de escribir
    
    return () => clearTimeout(timer);
  }, [referenceValues]);
  
  useEffect(() => {
    if (isInitialized) return;
    
    // Función para verificar las condiciones
    const checkConditions = () => {
      const values = getValues();
      
      // Verificar si los campos obligatorios tienen valores
      const hasStartDate = !!values.startsAt;
      const hasEndDate = !!values.expiresAt;
      const hasHotel = !!selectedHotel;
      const hasLockers = lockerRequirements.some(req => req.quantity > 0);
      
      // Verificar que todos los campos de referencia obligatorios tienen valor
      let allRequiredReferencesHaveValue = true;
      
      if (referenceFields.length > 0) {
        // Solo verificar campos explícitamente marcados como isMandatory
        const requiredFields = referenceFields.filter(field => field.isMandatory === true);
        
        allRequiredReferencesHaveValue = requiredFields.every(field => {
          const value = referenceValues[field.key];
          return value !== undefined && value !== null && value !== '';
        });
      }
      
      // Si todos los campos obligatorios están completos y no hemos marcado el form como listo
      if (hasStartDate && hasEndDate && hasHotel && hasLockers && allRequiredReferencesHaveValue) {
        if (!formState.isDirty) {
          // Marcar el formulario como dirty si aún no lo está
          setValue('_ready', true, { shouldDirty: true });
        }
        setIsInitialized(true);
      }
    };
    
    // Usar un timeout para asegurar que todo esté cargado
    const timer = setTimeout(checkConditions, 300);
    
    return () => clearTimeout(timer);
  }, [
    formState.isDirty,
    getValues,
    setValue,
    selectedHotel,
    lockerRequirements,
    referenceFields,
    debouncedReferenceValues,
    isInitialized
  ]);
};

// ============================================
// COMPONENTE: FormValidationHandler
// ============================================

export const FormValidationHandler: React.FC<FormValidationHandlerProps> = ({
  selectedHotel,
  lockerRequirements,
  referenceFields,
  referenceValues
}) => {
  useFormValidation(selectedHotel, lockerRequirements, referenceFields, referenceValues);
  return null;
};

// ============================================
// HOOK: useFormValidationAllRequired
// ============================================

export const useFormValidationAllRequired = (
  selectedHotel: Hotel | null,
  lockerRequirements: LockerRequirement[],
  referenceFields: ReferenceField[],
  referenceValues: Record<string, any>
) => {
  const { formState, getValues, setValue } = useFormContext();
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (isInitialized) return;
    
    const checkConditions = () => {
      const values = getValues();
      
      const hasStartDate = !!values.startsAt;
      const hasEndDate = !!values.expiresAt;
      const hasHotel = !!selectedHotel;
      const hasLockers = lockerRequirements.some(req => req.quantity > 0);
      
      // ASUME QUE TODOS LOS CAMPOS DE REFERENCIA SON OBLIGATORIOS
      const allReferencesHaveValue = referenceFields.length === 0 || 
        referenceFields.every(field => {
          const value = referenceValues[field.key];
          return value !== undefined && value !== null && value !== '';
        });
      
      if (hasStartDate && hasEndDate && hasHotel && hasLockers && allReferencesHaveValue) {
        if (!formState.isDirty) {
          setValue('_ready', true, { shouldDirty: true });
        }
        setIsInitialized(true);
      }
    };
    
    const timer = setTimeout(checkConditions, 300);
    return () => clearTimeout(timer);
  }, [
    formState.isDirty,
    getValues,
    setValue,
    selectedHotel,
    lockerRequirements,
    referenceFields,
    referenceValues,
    isInitialized
  ]);
};

// ============================================
// COMPONENTE: FormValidationAllRefsRequired
// ============================================

export const FormValidationAllRefsRequired: React.FC<FormValidationHandlerProps> = ({
  selectedHotel,
  lockerRequirements,
  referenceFields,
  referenceValues
}) => {
  useFormValidationAllRequired(selectedHotel, lockerRequirements, referenceFields, referenceValues);
  return null;
};

// ============================================
// COMPONENTE: SimpleEnableWhenValid
// ============================================

export const SimpleEnableWhenValid: React.FC = () => {
  const { formState, setValue } = useFormContext();
  
  useEffect(() => {
    // Si hay valores por defecto y el formulario es válido, marcarlo como dirty
    if (!formState.isDirty && Object.keys(formState.defaultValues || {}).length > 0) {
      // Pequeño delay para asegurar que los valores por defecto se han aplicado
      const timer = setTimeout(() => {
        setValue('_initialized', true, { shouldDirty: true });
      }, 0);
      
      return () => clearTimeout(timer);
    }
  }, [formState.isDirty, formState.defaultValues, setValue]);
  
  return null;
};